import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import {
  createAnswerItemsFromValues,
  getAnswerValuesFromItems
} from 'components/Assessments/helpers'
import { hasSelectedIntervention } from 'components/Assessments/utils/interventions'
import { PlanOfCareQuestionnaireItem } from './questionnaire'

/**
 * Plan of Care subsection adapter for SOC Assessment
 * Handles goals and interventions extraction and transformation
 */
export class PlanOfCareAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options: AdapterOptions
  ): Partial<SocFormData> {
    // Extract goals from flat answers
    const goals = getAnswerValuesFromItems(
      Object.values(flatAnswers),
      PlanOfCareQuestionnaireItem.PLAN_OF_CARE_GOALS_GOAL
    )

    // Extract interventions from flat answers
    const interventionAnswers =
      flatAnswers[PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO]
        ?.answer ?? []
    const interventions = interventionAnswers?.map((interventionAnswerItem) => {
      const interventionInfoItem = interventionAnswerItem.item ?? []

      const interventionId = getAnswerValuesFromItems(
        interventionInfoItem,
        PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INTERVENTION
      )?.at(0)

      const interventionCategory = getAnswerValuesFromItems(
        interventionInfoItem,
        PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_CATEGORY
      )?.at(0)

      const interventionGoal = getAnswerValuesFromItems(
        interventionInfoItem,
        PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_GOAL
      )?.at(0)

      const interventionTask = getAnswerValuesFromItems(
        interventionInfoItem,
        PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_TASK
      )?.at(0)

      const interventionIndication = getAnswerValuesFromItems(
        interventionInfoItem,
        PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INDICATION
      )?.at(0)

      const interventionType = getAnswerValuesFromItems(
        interventionInfoItem,
        PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_TYPE
      )?.at(0)

      const interventionInstruction = getAnswerValuesFromItems(
        interventionInfoItem,
        PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INSTRUCTION
      )?.at(0)

      const interventionPatientCenteredGoal = getAnswerValuesFromItems(
        interventionInfoItem,
        PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_PATIENT_CENTERED_GOAL
      )?.at(0)

      const interventionAdditionalDetails = getAnswerValuesFromItems(
        interventionInfoItem,
        PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_ADDITIONAL_DETAILS
      )?.at(0)

      const interventionVisits = getAnswerValuesFromItems(
        interventionInfoItem,
        PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_VISITS
      )

      const interventionFrequency = getAnswerValuesFromItems(
        interventionInfoItem,
        PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_FREQUENCY
      )

      return {
        id: interventionId,
        category: interventionCategory,
        interventionGoal: interventionGoal,
        task: interventionTask,
        indication: interventionIndication,
        type: interventionType,
        instruction: interventionInstruction,
        patientCenteredGoal: interventionPatientCenteredGoal,
        additionalDetails: interventionAdditionalDetails,
        visitNumbers: interventionVisits,
        frequency: interventionFrequency
      }
    })

    return {
      [SocFormField.GOALS]: goals,
      [SocFormField.INTERVENTIONS]: interventions
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const planOfCareAnswers: FlatAnswerMutation[] = []

    // Build goals flat answers
    if (this.formDataFieldExists(SocFormField.GOALS)) {
      const goals = this.getFormDataField(SocFormField.GOALS)
      if (goals) {
        planOfCareAnswers.push({
          linkId: PlanOfCareQuestionnaireItem.PLAN_OF_CARE_GOALS_GOAL,
          value: goals.filter((goal) => goal)
        })
      }
    }

    // Build interventions flat answers
    if (this.formDataFieldExists(SocFormField.INTERVENTIONS)) {
      const interventions = this.getFormDataField(SocFormField.INTERVENTIONS)
      if (interventions) {
        const interventionAnswers = interventions
          .filter(hasSelectedIntervention)
          .map((intervention) => ({
            item: createAnswerItemsFromValues([
              [
                PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INTERVENTION,
                intervention.id
              ],
              [
                PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_CATEGORY,
                intervention.category
              ],
              [
                PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_GOAL,
                intervention.interventionGoal
              ],
              [
                PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_TASK,
                intervention.task
              ],
              [
                PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INDICATION,
                intervention.indication
              ],
              [
                PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_TYPE,
                intervention.type
              ],
              [
                PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INSTRUCTION,
                intervention.instruction
              ],
              [
                PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_PATIENT_CENTERED_GOAL,
                intervention.patientCenteredGoal
              ],
              [
                PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_ADDITIONAL_DETAILS,
                intervention.additionalDetails
              ],
              [
                PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_VISITS,
                intervention.visitNumbers
              ],
              [
                PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_FREQUENCY,
                intervention.frequency
              ]
            ])
          }))

        planOfCareAnswers.push({
          linkId: PlanOfCareQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO,
          value: interventionAnswers
        })
      }
    }

    return planOfCareAnswers
  }
}
